import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, Marker, Polyline, TileLayer, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { ArrowLeft, FileUp, Image, RotateCcw, Save, Undo2, X } from 'lucide-react';
import { createRoute, fetchRouteCreatorPermission } from '../../api/move';
import type { RouteType } from '../../api/move';
import styles from '../../styles/Move.module.css';
import 'leaflet/dist/leaflet.css';

type Point = { lat: number; lng: number };
type GpxImport = {
  points: Point[];
  name?: string;
  description?: string;
  type?: RouteType;
  elevationGain?: number;
  durationMin?: number;
  startName?: string;
};

const SEGOVIA: [number, number] = [40.9489, -4.1182];
const TYPES: { key: RouteType; label: string }[] = [
  { key: 'walk', label: 'Trekking' },
  { key: 'run', label: 'Running' },
  { key: 'bike', label: 'Ciclismo' },
];

const routePointIcon = L.divIcon({
  className: 'move-route-create-pin',
  html: '<div style="width:16px;height:16px;border-radius:50%;background:hsl(0 72% 44%);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.25);"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

function formatDistance(m: number) {
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${m} m`;
}

function toRad(value: number) {
  return (value * Math.PI) / 180;
}

function distanceMeters(a: Point, b: Point) {
  const r = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function measure(points: Point[]) {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += distanceMeters(points[i - 1], points[i]);
  }
  return Math.round(total);
}

function textFrom(parent: Document | Element, selector: string) {
  return parent.querySelector(selector)?.textContent?.trim() || '';
}

function inferType(value: string): RouteType | null {
  const normalized = value.toLowerCase();
  if (/(bike|bici|bicicleta|cycling|ciclismo|mtb|btt)/.test(normalized)) return 'bike';
  if (/(run|running|correr|carrera|trail running)/.test(normalized)) return 'run';
  if (/(walk|hike|sender|trek|caminar|andar|paseo|trekking)/.test(normalized)) return 'walk';
  return null;
}

function elevationGain(nodes: Element[]) {
  let gain = 0;
  let prev: number | null = null;
  nodes.forEach(node => {
    const ele = Number(textFrom(node, 'ele'));
    if (!Number.isFinite(ele)) return;
    if (prev != null && ele > prev) gain += ele - prev;
    prev = ele;
  });
  return Math.round(gain);
}

function durationFromTimes(nodes: Element[]) {
  const times = nodes
    .map(node => {
      const raw = textFrom(node, 'time');
      const ms = raw ? Date.parse(raw) : NaN;
      return Number.isFinite(ms) ? ms : null;
    })
    .filter((value): value is number => value != null);
  if (times.length < 2) return null;
  return Math.max(1, Math.round((times[times.length - 1] - times[0]) / 60000));
}

function parseGpx(text: string): GpxImport {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  const parserError = doc.querySelector('parsererror');
  if (parserError) throw new Error('GPX inválido');
  const nodes = Array.from(doc.querySelectorAll('trkpt, rtept'));
  const points = nodes
    .map(node => ({
      lat: Number(node.getAttribute('lat')),
      lng: Number(node.getAttribute('lon')),
    }))
    .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  if (points.length < 2) throw new Error('El GPX necesita al menos dos puntos');

  const name = textFrom(doc, 'trk > name') || textFrom(doc, 'rte > name') || textFrom(doc, 'metadata > name');
  const description = textFrom(doc, 'trk > desc') || textFrom(doc, 'rte > desc') || textFrom(doc, 'metadata > desc');
  const typeText = textFrom(doc, 'trk > type') || textFrom(doc, 'rte > type') || `${name} ${description}`;
  const startName = textFrom(doc, 'wpt > name') || textFrom(doc, 'rtept > name');

  return {
    points,
    name,
    description,
    type: inferType(typeText) || undefined,
    elevationGain: elevationGain(nodes),
    durationMin: durationFromTimes(nodes) || undefined,
    startName,
  };
}

function ClickToAdd({ onAdd }: { onAdd: (point: Point) => void }) {
  useMapEvents({
    click(event) {
      onAdd({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
  });
  return null;
}

export default function RouteCreatePage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<RouteType>('walk');
  const [difficulty, setDifficulty] = useState(2);
  const [elevation, setElevation] = useState('');
  const [duration, setDuration] = useState('');
  const [startName, setStartName] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingPermission, setCheckingPermission] = useState(true);
  const [canCreateRoutes, setCanCreateRoutes] = useState(false);

  const distance = useMemo(() => measure(points), [points]);
  const positions = useMemo<[number, number][]>(() => points.map(p => [p.lat, p.lng]), [points]);
  const center = positions[0] ?? SEGOVIA;

  useEffect(() => {
    fetchRouteCreatorPermission()
      .then(setCanCreateRoutes)
      .catch(() => setCanCreateRoutes(false))
      .finally(() => setCheckingPermission(false));
  }, []);

  async function handleGpx(file: File | null) {
    if (!file) return;
    setError(null);
    try {
      const text = await file.text();
      const imported = parseGpx(text);
      setPoints(imported.points);
      if (!title) setTitle(imported.name || file.name.replace(/\.[^.]+$/, ''));
      if (!description && imported.description) setDescription(imported.description);
      if (!elevation && imported.elevationGain != null) setElevation(String(imported.elevationGain));
      if (!duration && imported.durationMin != null) setDuration(String(imported.durationMin));
      if (!startName && imported.startName) setStartName(imported.startName);
      if (imported.type) setType(imported.type);
    } catch (e: any) {
      setError(e?.message || 'No se pudo leer el GPX');
    }
  }

  function handleImage(file: File | null) {
    setImageFile(file);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  }

  async function handleSubmit() {
    setError(null);
    if (!title.trim()) {
      setError('Pon un título para la ruta');
      return;
    }
    if (points.length < 2) {
      setError('Dibuja la ruta en el mapa o sube un GPX');
      return;
    }
    setBusy(true);
    try {
      const result = await createRoute({
        titulo: title.trim(),
        descripcion: description.trim() || null,
        tipo: type,
        dificultad: difficulty,
        distancia_m: distance,
        desnivel_m: parseInt(elevation, 10) || 0,
        duracion_min: parseInt(duration, 10) || 0,
        ubicacion_inicio_nombre: startName.trim() || null,
        imagen: imageFile,
        geometry: {
          type: 'LineString',
          coordinates: points.map(point => [point.lng, point.lat]),
        },
      });
      navigate(`/move/routes/${result.route_id}`, { replace: true });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'No se pudo crear la ruta');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`move-app ${styles.page}`}>
      <header className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroOrb} />
        <div className={styles.heroInner}>
          <button
            onClick={() => navigate('/move/routes')}
            style={{
              background: 'rgba(255,255,255,0.18)', border: 0, color: '#fff',
              padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
            }}
          >
            <ArrowLeft size={14} /> Rutas
          </button>
          <h1 className={styles.greetingName} style={{ marginTop: 12 }}>Crear ruta</h1>
          <p className={styles.greetingLabel} style={{ marginTop: 4 }}>
            Dibuja sobre el mapa o importa un GPX
          </p>
        </div>
      </header>

      {!checkingPermission && !canCreateRoutes && (
        <section className={styles.section} style={{ marginTop: '-0.5rem' }}>
          <div className={styles.card} style={{ textAlign: 'center', display: 'grid', gap: 12 }}>
            <p className={styles.cardTitle}>No tienes permisos para crear rutas</p>
            <p className={styles.cardSubtitle}>
              Por defecto las rutas las gestiona Human. Un administrador puede activarte este permiso.
            </p>
            <button className={styles.btnPrimary} type="button" onClick={() => navigate('/move/routes')}>
              Volver a rutas
            </button>
          </div>
        </section>
      )}

      {checkingPermission && (
        <section className={styles.section} style={{ marginTop: '-0.5rem' }}>
          <p className={styles.empty}>Comprobando permisos...</p>
        </section>
      )}

      {canCreateRoutes && (
      <>
      <section className={styles.section} style={{ marginTop: '-0.5rem' }}>
        <div className={styles.card} style={{ display: 'grid', gap: 12 }}>
          <div>
            <label className={styles.label}>Título</label>
            <input className={styles.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Ej. Circular por la Alameda" />
          </div>

          <div>
            <label className={styles.label}>Descripción</label>
            <textarea className={styles.input} rows={3} value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          <div className={styles.goalFormRow}>
            <div>
              <label className={styles.label}>Tipo</label>
              <select className={styles.input} value={type} onChange={e => setType(e.target.value as RouteType)}>
                {TYPES.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
              </select>
            </div>
            <div>
              <label className={styles.label}>Dificultad</label>
              <select className={styles.input} value={difficulty} onChange={e => setDifficulty(Number(e.target.value))}>
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}/5</option>)}
              </select>
            </div>
          </div>

          <div className={styles.goalFormRow}>
            <div>
              <label className={styles.label}>Desnivel (m)</label>
              <input className={styles.input} type="number" inputMode="numeric" value={elevation} onChange={e => setElevation(e.target.value)} />
            </div>
            <div>
              <label className={styles.label}>Duración (min)</label>
              <input className={styles.input} type="number" inputMode="numeric" value={duration} onChange={e => setDuration(e.target.value)} placeholder="Auto" />
            </div>
          </div>

          <div>
            <label className={styles.label}>Lugar de inicio</label>
            <input className={styles.input} value={startName} onChange={e => setStartName(e.target.value)} placeholder="Ej. Segovia" />
          </div>

          <div>
            <label className={styles.label}>Imagen de portada</label>
            {imagePreview ? (
              <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden' }}>
                <img src={imagePreview} alt="Vista previa" style={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }} />
                <button
                  type="button"
                  onClick={() => handleImage(null)}
                  aria-label="Quitar imagen"
                  style={{
                    position: 'absolute', top: 10, right: 10, width: 34, height: 34,
                    borderRadius: 999, border: 0, background: 'rgba(0,0,0,0.45)',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className={styles.btnGhost} style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Image size={16} /> Añadir imagen
                <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={e => handleImage(e.target.files?.[0] ?? null)} />
              </label>
            )}
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Trazado</h2>
          <span className={styles.sectionLink}>{points.length} puntos · {formatDistance(distance)}</span>
        </div>

        <div style={{ borderRadius: 'var(--move-radius)', overflow: 'hidden', boxShadow: 'var(--move-shadow-card)', height: 280 }}>
          <MapContainer center={center} zoom={13} style={{ width: '100%', height: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ClickToAdd onAdd={point => setPoints(prev => [...prev, point])} />
            {positions.length > 1 && (
              <Polyline positions={positions} pathOptions={{ color: 'hsl(0 72% 44%)', weight: 5, opacity: 0.9, lineCap: 'round' }} />
            )}
            {positions.map((position, index) => (
              <Marker key={`${position[0]}-${position[1]}-${index}`} position={position} icon={routePointIcon} />
            ))}
          </MapContainer>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 12 }}>
          <label className={styles.btnGhost} style={{ textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <FileUp size={15} /> GPX
            <input type="file" accept=".gpx,application/gpx+xml,text/xml" style={{ display: 'none' }} onChange={e => handleGpx(e.target.files?.[0] ?? null)} />
          </label>
          <button className={styles.btnGhost} type="button" onClick={() => setPoints(prev => prev.slice(0, -1))} disabled={!points.length}>
            <Undo2 size={15} /> Deshacer
          </button>
          <button className={styles.btnGhost} type="button" onClick={() => setPoints([])} disabled={!points.length}>
            <RotateCcw size={15} /> Reset
          </button>
        </div>
      </section>

      <section className={styles.section}>
        {error && <p style={{ color: 'hsl(348 90% 50%)', fontSize: 13 }}>{error}</p>}
        <button className={styles.btnPrimary} style={{ width: '100%' }} disabled={busy} onClick={handleSubmit}>
          <Save size={16} /> {busy ? 'Guardando…' : 'Publicar ruta'}
        </button>
      </section>
      </>
      )}
    </div>
  );
}
